import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { formatPageTitle } from 'ag-common-svc/public-api';
import { MenuItemsConstants } from 'ag-common-lib/public-api';
import { AdminCrmComponent } from './admin-crm.component';
import { AdminCrmRoutes } from './admin-crm.model';
import { GoogleAuthManagementGuard } from './google-auth-management/google-auth-management.guard';
import { UserRoleManagerGuard } from './user-roles-manager/user-role-manager.guard';
import { TaskListManagerGuard } from './task-list-manager/task-list-manager.guard';
import { SalesGoalsManagerGuard } from './sales-goals/sales-goals-manager.guard';
import { MgaSalesGoalsManagerGuard } from './mga-sales-goals/mga-sales-goals-manager.guard';
import { ReportsManagerGuard } from './reports/reports-manager.guard';
import { LogsManagerGuard } from './logs-manager/logs-manager.guard';
import { ImportRulesManagerGuard } from './import-rules-manager/import-rules-manager.guard';
import { ErrorsManagerGuard } from './errors-manager/errors-manager.guard';
import { LookupsManagerGuard } from './lookups-manager/lookups-manager.guard';
import { AuthStatsGuard } from './auth-stats/auth-stats.guard';
import { AgentMergeToolGuard } from './agent-merge-tool/agent-merge-tool.guard';
import { AddressesManagementGuard } from './addresses-management/addresses-management.guard';
import { UsersAgreementaListGuard } from './users-agreements-list/users-agreementa-list.guard';
import { UsersAgreementsMasterVersionsListGuard } from './users-agreements-master-versions-list/users-agreements-master-versions-list.guard';

const administrationMenuItems = MenuItemsConstants.crm.administration;

const administrationPageTitle = (pageTitle: string) =>
  formatPageTitle([administrationMenuItems.administration, pageTitle]);

const administrationNestedPageTitle = (sectionTitle: string, pageTitle: string) =>
  formatPageTitle([administrationMenuItems.administration, sectionTitle, pageTitle]);

const routes: Routes = [
  {
    path: '',
    component: AdminCrmComponent,
    children: [
      {
        path: AdminCrmRoutes.TaskListManager,
        title: administrationPageTitle(administrationMenuItems.taskLists),
        loadChildren: () =>
          import('./task-list-manager/task-list-manager.module').then(mod => mod.TaskListManagerModule),
        canActivate: [TaskListManagerGuard],
      },
      {
        path: AdminCrmRoutes.LookupsManager,
        title: administrationPageTitle(administrationMenuItems.lookupsManager),
        loadChildren: () => import('./lookups-manager/lookups-manager.module').then(mod => mod.LookupsManagerModule),
        canActivate: [LookupsManagerGuard],
      },
      {
        path: AdminCrmRoutes.ImportRulesManager,
        title: administrationPageTitle(administrationMenuItems.importRules),
        loadChildren: () =>
          import('./import-rules-manager/import-rules-manager.module').then(mod => mod.ImportRulesManagerModule),
        canActivate: [ImportRulesManagerGuard],
      },
      {
        path: AdminCrmRoutes.ErrorsManager,
        title: administrationPageTitle(administrationMenuItems.errorsManager),
        loadChildren: () => import('./errors-manager/errors-manager.module').then(mod => mod.ErrorsManagerModule),
        canActivate: [ErrorsManagerGuard],
      },
      {
        path: AdminCrmRoutes.LogsManager,
        title: administrationPageTitle(administrationMenuItems.logsManager),
        loadChildren: () => import('./logs-manager/logs-manager.module').then(mod => mod.LogsManagerModule),
        canActivate: [LogsManagerGuard],
      },
      {
        path: AdminCrmRoutes.UserRolesManager,
        title: administrationNestedPageTitle(administrationMenuItems.userRoles, administrationMenuItems.assignment),
        loadChildren: () =>
          import('./user-roles-manager/user-roles-manager.module').then(mod => mod.UserRolesManagerModule),
        canActivate: [UserRoleManagerGuard],
      },
      {
        path: AdminCrmRoutes.ReportsManager,
        title: administrationPageTitle(administrationMenuItems.reportsManager),
        loadChildren: () => import('./reports/reports.module').then(mod => mod.ReportsModule),
        canActivate: [ReportsManagerGuard],
      },
      {
        path: AdminCrmRoutes.AgentMergeTool,
        title: administrationPageTitle(administrationMenuItems.agentMergeTool),
        loadChildren: () => import('./agent-merge-tool/agent-merge-tool.module').then(mod => mod.AgentMergeToolModule),
        canActivate: [AgentMergeToolGuard],
      },
      {
        path: AdminCrmRoutes.SalesGoals,
        redirectTo: AdminCrmRoutes.AgentSalesGoals,
        pathMatch: 'full',
      },
      {
        path: AdminCrmRoutes.AgentSalesGoals,
        title: administrationNestedPageTitle(administrationMenuItems.salesGoals, administrationMenuItems.agentGoals),
        loadChildren: () => import('./sales-goals/sales-goals.module').then(mod => mod.SalesGoalsModule),
        canActivate: [SalesGoalsManagerGuard],
      },
      {
        path: AdminCrmRoutes.MgaSalesGoals,
        title: administrationNestedPageTitle(administrationMenuItems.salesGoals, administrationMenuItems.mgaGoals),
        loadChildren: () => import('./mga-sales-goals/mga-sales-goals.module').then(mod => mod.MgaSalesGoalsModule),
        canActivate: [MgaSalesGoalsManagerGuard],
      },
      {
        path: AdminCrmRoutes.RoleManager,
        title: administrationNestedPageTitle(administrationMenuItems.userRoles, administrationMenuItems.management),
        loadChildren: () => import('./role-manager/role-manager.module').then(mod => mod.RoleManagerModule),
      },
      {
        path: AdminCrmRoutes.GoogleAuthManagement,
        title: administrationPageTitle(administrationMenuItems.googleAuthManagement),
        loadChildren: () =>
          import('./google-auth-management/google-auth-management.module').then(mod => mod.GoogleAuthManagementModule),
        canActivate: [GoogleAuthManagementGuard],
      },
      {
        path: AdminCrmRoutes.AuthStats,
        title: administrationPageTitle(administrationMenuItems.authStats),
        loadChildren: () => import('./auth-stats/auth-stats.module').then(mod => mod.AuthStatsModule),
        canActivate: [AuthStatsGuard],
      },
      // TODO Guard create
      {
        path: AdminCrmRoutes.AgentMergeTool,
        title: administrationPageTitle(administrationMenuItems.agentMergeTool),
        loadChildren: () => import('./agent-merge-tool/agent-merge-tool.module').then(mod => mod.AgentMergeToolModule),
        canActivate: [AuthStatsGuard],
      },
      {
        path: AdminCrmRoutes.AddressesManagement,
        title: administrationPageTitle(administrationMenuItems.addressesManagement),
        loadChildren: () =>
          import('./addresses-management/addresses-management.module').then(mod => mod.AddressesManagementModule),
        canActivate: [AddressesManagementGuard],
      },
      {
        path: AdminCrmRoutes.MasterVersionsList,
        title: administrationNestedPageTitle(
          administrationMenuItems.usersAgreements,
          administrationMenuItems.masterVersionsList,
        ),
        loadChildren: () =>
          import('./users-agreements-master-versions-list/users-agreements-master-versions-list.module').then(
            mod => mod.UsersAgreementsMasterVersionsListModule,
          ),
        canActivate: [UsersAgreementsMasterVersionsListGuard],
      },
      {
        path: AdminCrmRoutes.SubversionsList,
        title: administrationNestedPageTitle(
          administrationMenuItems.usersAgreements,
          administrationMenuItems.subversionsList,
        ),
        loadChildren: () =>
          import('./users-agreements-list/users-agreements-list.module').then(mod => mod.UsersAgreementsListModule),
        canActivate: [UsersAgreementaListGuard],
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  providers: [],
  exports: [RouterModule],
})
export class AdminCrmRoutingModule {}
